import { Injectable, signal } from '@angular/core';

/**
 * Le pays dont on affiche l'imagerie satellite, pour toutes les cartes des deux applications.
 *
 * <p>Il y a une raison à ce que ce soit un service racine unique plutôt qu'un signal par écran :
 * chaque bibliothèque a son propre `MapStateService`, et une carte n'a pas toujours sous la main
 * l'objet qui porte l'adresse — la page détail d'un tracker connaît le tracker, la fenêtre d'aperçu
 * d'un site ne connaît qu'un identifiant. Répartir le pays sur cinq signaux revenait à en oublier
 * un : c'est exactement ce qui laissait des cartes en France sur un site tchèque.
 *
 * <p>La règle est donc : l'écran qui charge un site ou un tracker déclare le pays une fois, et
 * toutes les cartes suivent. C'est délibérément persistant d'une carte à l'autre — quitter le
 * détail d'un site pour celui d'un de ses trackers ne doit pas repasser en France le temps que la
 * requête revienne.
 */
@Injectable({ providedIn: 'root' })
export class SatelliteCountryService {
  /**
   * Pays courant, en code ISO ou en clair. Vide, on retombe sur la France : le parc est très
   * majoritairement français, et une orthophotographie nationale ne répond que chez elle.
   */
  readonly country = signal<string | null>(null);

  /**
   * Déclare le pays depuis l'adresse d'un site.
   *
   * <p>Accepte l'objet entier plutôt que la chaîne pour que les appelants n'aient pas à répéter la
   * même cascade d'optionnels, et surtout pour qu'un site sans adresse remette bien la valeur par
   * défaut au lieu de laisser traîner le pays de l'écran précédent.
   */
  setFromAddress(address: { country?: string | null } | null | undefined): void {
    this.country.set(address?.country ?? null);
  }
}
